import { useState } from "react";
import { Eye } from "lucide-react";
import { applicationsV2, type ApplicationV2Detail, type V2Bag, type V2Value } from "@/features/applications/api/applications.api";
import { Notice } from "@/components/Notice";
import { useConfirm } from "@/components/ConfirmDialog";
import { useFileViewer } from "@/components/FileViewerDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { type FieldDef } from "../model";
import { FieldGrid } from "../FieldGrid";
import { SectionCard, ReqBadge } from "../primitives";
import { DocumentDropZone } from "../DocumentDropZone";
import { useReadOnly } from "../readonly";
import { formatDateTime } from "@/utils/dates";
import { docSizeError, MAX_DOC_FILE_MB } from "@/utils/docFile";
import { downscaleImage } from "@/utils/image";

const ACCEPT = [
  "image/jpeg", ".jpg", ".jpeg",
  "image/png", ".png",
  "image/gif", ".gif",
  "image/webp", ".webp",
  "image/bmp", ".bmp",
  "image/tiff", ".tif", ".tiff",
  "image/heic", ".heic",
  "image/heif", ".heif",
  "image/avif", ".avif",
  "image/svg+xml", ".svg",
  "application/pdf", ".pdf",
  "application/msword", ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", ".docx",
  "application/vnd.ms-excel", ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ".xlsx",
].join(",");

const ACCEPT_HINT = `Images, PDF, Word or Excel up to ${MAX_DOC_FILE_MB} MB`;

type UploadZone = "statements" | "attach";

const UNLABELED = "Other";

const DOC_TYPES = [
  "DriverLicense", "VoidedCheck", "SignedMerchantApplication", "ArticlesOfIncorporation",
  "TaxReturn", "BusinessLicense", "FinancialStatements", "BankLetter", "BankRatingForm",
  "ProcessingStatement", "MarketingMaterials", "W9", "SiteSurvey", "ProofOfBusiness",
  "ProofOfNonProfit", "Proof501c3", "PetroAddendum", "AchAddendum",
];

const DOC_TYPE_LABELS: Record<string, string> = {
  SignedMerchantApplication: "Signed Merchant Application",
  DriverLicense: "Driver's License",
  VoidedCheck: "Voided Check",
  ArticlesOfIncorporation: "Articles of Incorporation",
  TaxReturn: "Tax Return",
  BusinessLicense: "Government Issued Business License",
  FinancialStatements: "Business Financial Statements",
  BankLetter: "Bank Letter",
  BankRatingForm: "Bank Rating Form",
  ProcessingStatement: "Processing Statement",
  MarketingMaterials: "Marketing Materials",
  W9: "IRS W-9 Form",
  SiteSurvey: "Site Survey",
  ProofOfBusiness: "Proof of Business",
  ProofOfNonProfit: "Proof of Non-Profit Status",
  Proof501c3: "Proof of 501(c)(3) Status",
  PetroAddendum: "Petro Addendum",
  AchAddendum: "ACH Addendum",
  Underwriting: "Underwriting",
  Other: "Other",
};

export function DocumentsStep({
  appId, documents, onChanged, fields, bag, onChange,
}: {
  appId: string;
  documents: ApplicationV2Detail["documents"];
  onChanged: () => Promise<void>;
  fields: FieldDef[];
  bag: V2Bag;
  onChange: (key: string, value: V2Value) => void;
}) {
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [relabeling, setRelabeling] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [uploadingZone, setUploadingZone] = useState<UploadZone | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const confirm = useConfirm();
  const viewFile = useFileViewer();
  const readOnly = useReadOnly();

  const acceptedBefore = bag.hasAcceptedCardsBefore === true;
  const processingStatements = documents.filter((d) => d.documentType === "ProcessingStatement");

  async function uploadAs(files: File[], type: string, zone: UploadZone) {
    setBusy(true);
    setUploadingZone(zone);
    setErr(null);
    const problems: string[] = [];
    let uploaded = 0;
    try {
      for (const [i, file] of files.entries()) {
        setProgress(files.length > 1 ? `Uploading ${i + 1} of ${files.length}…` : null);
        const sizeError = docSizeError(file);
        if (sizeError) {
          problems.push(`${file.name}: ${sizeError}`);
          continue;
        }
        try {
          await applicationsV2.uploadDocument(appId, await downscaleImage(file), type);
          uploaded++;
        } catch (e) {
          problems.push(`${file.name}: ${e instanceof Error ? e.message : "Upload failed."}`);
        }
      }
      if (uploaded > 0) await onChanged();
    } finally {
      setBusy(false);
      setUploadingZone(null);
      setProgress(null);
      if (problems.length > 0) setErr(problems.join(" "));
    }
  }

  function view(docId: string, filename: string) {
    setErr(null);
    viewFile({
      name: filename,
      load: () => applicationsV2.documentFile(appId, docId),
      errorMessage: "Could not open document.",
    });
  }

  async function download(docId: string, filename: string) {
    setErr(null);
    try {
      await applicationsV2.downloadDocument(appId, docId, filename);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Download failed.");
    }
  }

  async function relabel(docId: string, type: string) {
    setErr(null);
    setLabels((m) => ({ ...m, [docId]: type }));
    setRelabeling((ids) => new Set(ids).add(docId));
    try {
      await applicationsV2.relabelDocument(appId, docId, type);
    } catch (e) {
      setLabels((m) => {
        const next = { ...m };
        delete next[docId];
        return next;
      });
      setErr(e instanceof Error ? e.message : "Couldn't change the document type.");
      setRelabeling((ids) => {
        const next = new Set(ids);
        next.delete(docId);
        return next;
      });
      return;
    }
    try {
      await onChanged();
    } catch {
      return;
    } finally {
      setRelabeling((ids) => {
        const next = new Set(ids);
        next.delete(docId);
        return next;
      });
    }
  }

  async function remove(docId: string) {
    if (!(await confirm({ description: "Remove this document?", confirmLabel: "Remove", destructive: true }))) return;
    setBusy(true);
    setErr(null);
    try {
      await applicationsV2.deleteDocument(appId, docId);
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Delete failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      {err && <Notice tone="error">{err}</Notice>}

      <SectionCard title="Details">
        <FieldGrid fields={fields} bag={bag} onChange={onChange} />
        {acceptedBefore && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Please provide the most recent 3 months of processing statements
              </span>
              {!readOnly && <ReqBadge />}
            </div>
            {!readOnly && (
              <DocumentDropZone
                accept={ACCEPT}
                busy={busy}
                uploading={uploadingZone === "statements"}
                progress={progress}
                hint={`${ACCEPT_HINT} · attached as ${DOC_TYPE_LABELS.ProcessingStatement}`}
                onFiles={(f) => uploadAs(f, "ProcessingStatement", "statements")}
              />
            )}
            <p className="text-xs text-slate-500 mt-1">
              {processingStatements.length === 0
                ? "No statements uploaded yet. You can pick all 3 at once."
                : `${processingStatements.length} statement${processingStatements.length === 1 ? "" : "s"} uploaded.`}
            </p>
          </div>
        )}
      </SectionCard>

      {!readOnly && (
      <SectionCard title="Attach documents">
        <p className="text-sm text-slate-500 -mt-2 mb-4">
          Drop files or browse. You can pick several at once. Driver's licenses and void checks are labeled automatically; choose a type for the rest below. Documents are stored privately.
        </p>
        <DocumentDropZone
          accept={ACCEPT}
          busy={busy}
          uploading={uploadingZone === "attach"}
          progress={progress}
          hint={ACCEPT_HINT}
          onFiles={(f) => uploadAs(f, UNLABELED, "attach")}
        />
      </SectionCard>
      )}

      <SectionCard title="Attached documents" contentClassName="p-0">
        {documents.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">No documents attached yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium uppercase tracking-wide">Type</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">Filename</TableHead>
                <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Size</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">Uploaded</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="text-slate-800">
                    <DocTypeCell
                      type={labels[d.id] ?? d.documentType}
                      readOnly={readOnly}
                      fromMerchant={d.fromMerchant === true}
                      disabled={busy || relabeling.has(d.id)}
                      onChange={(t) => relabel(d.id, t)}
                    />
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => view(d.id, d.filename)}
                      className="text-foreground hover:underline text-left"
                    >
                      {d.filename}
                    </button>
                  </TableCell>
                  <TableCell className="text-right text-slate-600 tabular-nums">{(d.sizeBytes / 1024).toFixed(0)} KB</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDateTime(d.uploadedAt)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-4">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-500 hover:text-slate-900"
                        onClick={() => view(d.id, d.filename)}
                        aria-label={`Preview ${d.filename}`}
                        title="Preview"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        onClick={() => download(d.id, d.filename)}
                        disabled={busy}
                      >
                        Download
                      </Button>
                      {!readOnly && (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          onClick={() => remove(d.id)}
                          disabled={busy}
                          className="h-auto p-0 text-destructive"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}

function DocTypeCell({ type, readOnly, fromMerchant, disabled, onChange }: {
  type: string;
  readOnly: boolean;
  fromMerchant: boolean;
  disabled: boolean;
  onChange: (type: string) => void;
}) {
  const labeled = type !== UNLABELED;
  if (fromMerchant) {
    return (
      <div>
        <div>{DOC_TYPE_LABELS[type] ?? type}</div>
        <div className="text-xs text-slate-500">From merchant</div>
      </div>
    );
  }
  if (readOnly) return <span className={labeled ? undefined : "text-slate-400"}>{labeled ? DOC_TYPE_LABELS[type] ?? type : "No type"}</span>;
  const options = labeled && !DOC_TYPES.includes(type) ? [type, ...DOC_TYPES] : DOC_TYPES;
  return (
    <Select value={labeled ? type : undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-8 w-full min-w-44 max-w-64 text-sm">
        <SelectValue placeholder="Choose type (optional)" />
      </SelectTrigger>
      <SelectContent>
        {options.map((t) => (
          <SelectItem key={t} value={t}>{DOC_TYPE_LABELS[t] ?? t}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
